import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDay, type MessageKey } from '@lensmira/i18n'

import type { ClaimCaptureClaim, ClaimCaptureData } from '../api/claim-capture.js'
import { Ltr, useTranslation } from '../i18n.js'
import { formatWireForDisplay, parseMajorInput, type WireMoney } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { claimStatusWord } from './PanelStatements.js'

/**
 * The panel's share of this sale. §6.13 `CLM-002`, `CLM-003`; §15.20 steps 2 and 3.
 *
 * > §15.20 step 3: the panel's share is "a receivable, not a payment"
 *
 * **So nothing on this panel takes money.** `claims.service.ts` never touches the `payment` table
 * and the order's balance is unchanged by a capture: what this records is that the payer owes part
 * of the bill and the customer owes the rest. The hint says so in the shop's own words, because a
 * split that looks like a tender is the mistake that leaves a counter thinking the sale is settled.
 * Taking the customer's part is still `TakePayment`, and the panel's money arriving later is the
 * remittance screen's.
 *
 * ---
 *
 * **A panel on the order, not a destination.** All 26 rail glyphs are taken and `rail.test.ts`
 * enforces it (`decisions/0026`, `decisions/0040`), and the order is the right place anyway: a
 * claim is raised against one sale, from the screen that already has that sale on it.
 *
 * **It pre-checks nothing it cannot see, and offers nothing the server will refuse.** `OFF-001a`
 * puts every write through the queue and `OFF-005` leaves a refused one `blocked` at its head,
 * stopping every write behind it, so one wrong offer stops the till. The division is:
 *
 * - whether this order can carry a claim, and which of the customer's plans will take it, is the
 *   server's answer, rendered as sent (`GET /orders/:orderId/claim`). A withheld plan shows its
 *   own reason instead of being hidden, because "Acme Gold is not offered" is a question the
 *   shopkeeper will otherwise ask the panel.
 * - which events are available on an existing claim is arithmetic on the figures the same read
 *   returned, in {@link claimActions}. Every bound there is `applyClaimEvent`'s own, read off the
 *   claim the server sent, so the two cannot disagree about what is in `denied`.
 *
 * **Two Appendix B rows, and the panel shows only what the session holds.** Capturing, submitting,
 * rebilling and moving a refusal to the customer are `claim_capture`, the wide counter row. A
 * write-off and a reduction are `panel_remittance_record`, because both give up money rather than
 * record it, so they are offered only to its three holders. A dispenser is shown the claim and the
 * four actions they may take, and never a button that would come back 403 and stop their queue.
 */

// ---------------------------------------------------------------------------------------------
// What the panel offers on an existing claim
// ---------------------------------------------------------------------------------------------

/** The event kinds this panel can offer. `remit` and `reverse_remittance` are not among them. */
export type ClaimActionKind =
  'submit' | 'resubmit' | 'transfer_to_customer' | 'write_off' | 'reduce'

export interface ClaimAction {
  readonly kind: ClaimActionKind
  readonly offered: boolean
  /** The most the server will accept, in minor units. `'0'` when the action is not offered. */
  readonly maxMinor: string
  /** An amount is required rather than defaulted. Only a reduction is. */
  readonly amountRequired: boolean
  readonly reasonRequired: boolean
  /** Which Appendix B row the route asks for. */
  readonly row: 'claim_capture' | 'panel_remittance_record'
}

const minor = (value: WireMoney): bigint => BigInt(value.minor)

/**
 * Which events `applyClaimEvent` will accept on this claim, and the bound on each.
 *
 * Every line here is that function's own precondition, taken from the figures the server sent
 * rather than recomputed:
 *
 * - `submit` only from `captured`, and only while something is open ("only a refund can still
 *   touch it")
 * - `resubmit` and `transfer_to_customer` move refused money, so both need `denied` above zero and
 *   neither may exceed it
 * - `write_off` takes at most what is still open, which is `outstanding + denied`
 * - `reduce` takes at most `claimed - reduced`, and is the one event allowed on a closed claim,
 *   because a refund arrives after the claim is paid
 *
 * Exported and pure so the table can be tested against the domain's own refusals without a server.
 */
export function claimActions(claim: ClaimCaptureClaim): readonly ClaimAction[] {
  const denied = minor(claim.denied)
  const open = minor(claim.outstanding) + denied
  const standing = minor(claim.claimed) - minor(claim.reduced)
  const action = (
    kind: ClaimActionKind,
    offered: boolean,
    max: bigint,
    over: { amountRequired?: boolean; reasonRequired?: boolean } = {},
  ): ClaimAction => ({
    kind,
    offered,
    maxMinor: (offered ? max : 0n).toString(),
    amountRequired: over.amountRequired === true,
    reasonRequired: over.reasonRequired === true,
    row: kind === 'write_off' || kind === 'reduce' ? 'panel_remittance_record' : 'claim_capture',
  })

  return [
    action('submit', claim.status === 'captured' && open > 0n, 0n),
    action('resubmit', denied > 0n, denied),
    action('transfer_to_customer', denied > 0n, denied, { reasonRequired: true }),
    action('write_off', open > 0n, open, { reasonRequired: true }),
    action('reduce', standing > 0n, standing, { amountRequired: true, reasonRequired: true }),
  ]
}

// ---------------------------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------------------------

export interface ClaimCaptureProps {
  /** The order on screen. The claim is raised against this sale and no other. */
  readonly orderId: string
  readonly load: (orderId: string) => Promise<ClaimCaptureData>
  /** Appendix B `claim_capture`: capture, submit, rebill, move a refusal to the customer. */
  readonly canCapture: boolean
  /** Appendix B `panel_remittance_record`: write off and reduce, which give up money. */
  readonly canSettle: boolean
  readonly timezone: string
  /** Re-read the order after a capture, because its panel share is new. */
  readonly onChanged?: () => void
}

/**
 * One key per action, written out rather than built from the kind.
 *
 * `apps/counter/test/i18n-static.test.ts` reads quoted key literals out of the source to find a
 * key the catalogue does not have, and a template literal is invisible to it: five keys assembled
 * from `claimCapture.action.${kind}` would read as unused by anything and are the shape a live
 * string gets deleted by.
 */
const ACTION_LABEL: Readonly<Record<ClaimActionKind, MessageKey>> = {
  submit: 'claimCapture.action.submit',
  resubmit: 'claimCapture.action.resubmit',
  transfer_to_customer: 'claimCapture.action.transferToCustomer',
  write_off: 'claimCapture.action.writeOff',
  reduce: 'claimCapture.action.reduce',
}

const ACTION_QUEUE: Readonly<Record<ClaimActionKind, MessageKey>> = {
  submit: 'claimCapture.queue.submit',
  resubmit: 'claimCapture.queue.resubmit',
  transfer_to_customer: 'claimCapture.queue.transferToCustomer',
  write_off: 'claimCapture.queue.writeOff',
  reduce: 'claimCapture.queue.reduce',
}

const show = (value: WireMoney): string => formatWireForDisplay(value.minor, value.currency)

export function ClaimCapture({
  orderId,
  load,
  canCapture,
  canSettle,
  timezone,
  onChanged,
}: ClaimCaptureProps): JSX.Element | null {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [data, setData] = useState<ClaimCaptureData | null>(null)
  const [failed, setFailed] = useState(false)
  const [planId, setPlanId] = useState('')
  const [reference, setReference] = useState('')
  const [grantedOn, setGrantedOn] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [chosen, setChosen] = useState<ClaimActionKind | null>(null)
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [invalid, setInvalid] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(() => {
    let cancelled = false
    setFailed(false)
    void load(orderId)
      .then((answer) => {
        if (!cancelled) setData(answer)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [load, orderId])

  useEffect(() => refresh(), [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setConfirming(false)
    setChosen(null)
    setAmount('')
    setReason('')
    setInvalid(null)
    refresh()
    onChanged?.()
  })

  const plan = data?.plans.find((row) => row.id === planId)

  const capture = useCallback(async () => {
    if (plan === undefined) return
    const write = (await enqueue({
      method: 'POST',
      path: '/claims',
      label: t('claimCapture.queueLabel', { panel: plan.panel }),
      body: {
        orderId,
        planId: plan.id,
        // Sent when both halves are there, and `raise` validates both: a reference with no day is
        // `DATE_INVALID`, which would be a blocked write for a form the screen could have held.
        ...(reference.trim() === '' || grantedOn === ''
          ? {}
          : { authorisation: { reference: reference.trim(), grantedOn } }),
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, grantedOn, orderId, plan, reference, t])

  const send = useCallback(
    async (action: ClaimAction, claim: ClaimCaptureClaim) => {
      let amountMinor: string | undefined
      if (amount !== '') {
        try {
          const parsed = parseMajorInput(amount, claim.claimed.currency)
          if (parsed <= 0n || parsed > BigInt(action.maxMinor)) {
            setInvalid(t('claimCapture.amountOutOfRange'))
            return
          }
          amountMinor = parsed.toString()
        } catch {
          // `MoneyInputError` and anything else both mean the same thing to the shopkeeper, and
          // neither is sent: `parseMajorInput` is what decides whether a typed figure is money.
          setInvalid(t('claimCapture.amountNotANumber'))
          return
        }
      } else if (action.amountRequired) {
        setInvalid(t('claimCapture.amountRequired'))
        return
      }
      if (action.reasonRequired && reason.trim() === '') {
        setInvalid(t('claimCapture.reasonRequired'))
        return
      }
      setInvalid(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/claims/${encodeURIComponent(claim.id)}/events`,
        label: t(ACTION_QUEUE[action.kind]),
        body: {
          // The lock, in the body, where the services read it (`queue/types.ts` says why it is not
          // a header). It is the version the figures on screen came from.
          expectedVersion: claim.version,
          kind: action.kind,
          ...(amountMinor === undefined ? {} : { amountMinor }),
          ...(action.reasonRequired ? { reason: reason.trim() } : {}),
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [amount, enqueue, reason, t],
  )

  if (!canCapture && !canSettle) return null

  const line = statusLine(t, snapshot, writeId, t('queue.subject.claim'))
  const claim = data?.claim
  const offeredPlans = (data?.plans ?? []).filter((row) => row.offered)
  const withheldPlans = (data?.plans ?? []).filter((row) => !row.offered)
  const actions =
    claim === undefined
      ? []
      : claimActions(claim).filter(
          (action) => action.offered && (action.row === 'claim_capture' ? canCapture : canSettle),
        )
  const active = actions.find((action) => action.kind === chosen)

  return (
    <section className="lm-screen lm-claim" aria-labelledby="claim-capture-heading">
      <h2 id="claim-capture-heading" className="lm-screen__subheading">
        {t('claimCapture.heading')}
      </h2>
      {/* §15.20 step 3, said once where it is read: this records a receivable and tenders nothing. */}
      <p className="lm-hint">{t('claimCapture.receivableNotPayment')}</p>

      {failed ? (
        <p className="lm-error">
          {t('claimCapture.loadFailed')}{' '}
          <button
            type="button"
            className="lm-button lm-button--secondary"
            onClick={() => {
              refresh()
            }}
          >
            {t('claimCapture.retry')}
          </button>
        </p>
      ) : data === null ? (
        <p className="lm-hint">{t('claimCapture.loading')}</p>
      ) : claim !== undefined ? (
        <>
          <dl className="lm-claim__figures">
            <dt>{t('claimCapture.status')}</dt>
            <dd>{claimStatusWord(t, claim.status)}</dd>
            <dt>{t('claimCapture.panelOwes')}</dt>
            <dd>{show(claim.outstanding)}</dd>
            <dt>{t('claimCapture.customerOwes')}</dt>
            <dd>{show(claim.customerShare)}</dd>
            <dt>{t('claimCapture.paid')}</dt>
            <dd>{show(claim.paid)}</dd>
            <dt>{t('claimCapture.denied')}</dt>
            <dd>{show(claim.denied)}</dd>
            <dt>{t('claimCapture.transferred')}</dt>
            <dd>{show(claim.transferred)}</dd>
            <dt>{t('claimCapture.writtenOff')}</dt>
            <dd>{show(claim.writtenOff)}</dd>
            <dt>{t('claimCapture.capturedOn')}</dt>
            <dd>{formatDay(claim.capturedOn, timezone, locale)}</dd>
          </dl>

          <table className="lm-claim__split">
            <caption>{t('claimCapture.splitCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('claimCapture.colLine')}</th>
                <th scope="col">{t('claimCapture.colPrice')}</th>
                <th scope="col">{t('claimCapture.colPanel')}</th>
                <th scope="col">{t('claimCapture.colCustomer')}</th>
              </tr>
            </thead>
            <tbody>
              {claim.lines.map((row) => (
                <tr key={row.orderLineId}>
                  <td>{row.category}</td>
                  <td>{show(row.price)}</td>
                  <td>{show(row.payer)}</td>
                  <td>{show(row.customer)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {actions.length === 0 ? (
            <p className="lm-empty">{t('claimCapture.nothingToDo')}</p>
          ) : (
            <div className="lm-claim__actions">
              {actions.map((action) => (
                <button
                  key={action.kind}
                  type="button"
                  className="lm-button lm-button--secondary"
                  // The kind, on the element. Every label here is a catalogue key, so a test that
                  // picked a button by its text would be asserting English that a translator is
                  // free to change, and `rail.test.ts` makes the same choice for the same reason.
                  data-kind={action.kind}
                  aria-pressed={chosen === action.kind}
                  onClick={() => {
                    setChosen(chosen === action.kind ? null : action.kind)
                    setAmount('')
                    setReason('')
                    setInvalid(null)
                  }}
                >
                  {t(ACTION_LABEL[action.kind])}
                </button>
              ))}
            </div>
          )}

          {active === undefined ? null : (
            <>
              {active.kind === 'submit' ? null : (
                <label className="lm-field">
                  <span className="lm-field__label">
                    {active.amountRequired
                      ? t('claimCapture.amountLabel')
                      : t('claimCapture.amountLabelOptional')}
                  </span>
                  <input
                    className="lm-input"
                    inputMode="decimal"
                    dir="ltr"
                    value={amount}
                    onChange={(e) => setAmount(e.currentTarget.value)}
                  />
                  <span className="lm-hint">
                    {t('claimCapture.amountMost', {
                      amount: show({ minor: active.maxMinor, currency: claim.claimed.currency }),
                    })}
                  </span>
                </label>
              )}
              {active.reasonRequired ? (
                <label className="lm-field">
                  <span className="lm-field__label">{t('claimCapture.reasonLabel')}</span>
                  <input
                    className="lm-input"
                    value={reason}
                    onChange={(e) => setReason(e.currentTarget.value)}
                  />
                </label>
              ) : null}
              {active.kind === 'write_off' ? (
                <p className="lm-field__warning">{t('claimCapture.writeOffMeans')}</p>
              ) : null}
              {invalid === null ? null : <p className="lm-error">{invalid}</p>}
              <button
                type="button"
                className="lm-button lm-button--primary"
                disabled={writeId !== null}
                onClick={() => {
                  void send(active, claim)
                }}
              >
                {t('claimCapture.send')}
              </button>
            </>
          )}
        </>
      ) : !canCapture ? (
        <p className="lm-hint">{t('claimCapture.noneYet')}</p>
      ) : !data.offered ? (
        /* The server's sentence, as sent: it names the order's status or its two totals. */
        <p className="lm-claim__withheld" dir="auto">
          {data.message ?? data.code ?? t('claimCapture.notOffered')}
        </p>
      ) : offeredPlans.length === 0 ? (
        <>
          <p className="lm-empty">{t('claimCapture.noPlans')}</p>
          <WithheldPlans rows={withheldPlans} t={t} />
        </>
      ) : (
        <>
          <label className="lm-field">
            <span className="lm-field__label">{t('claimCapture.planLabel')}</span>
            <select
              className="lm-input"
              value={planId}
              onChange={(e) => {
                setPlanId(e.currentTarget.value)
                setConfirming(false)
              }}
            >
              <option value="">{t('claimCapture.planUnchosen')}</option>
              {offeredPlans.map((row) => (
                <option key={row.id} value={row.id}>
                  {t('claimCapture.planOption', { panel: row.panel, plan: row.name })}
                </option>
              ))}
            </select>
          </label>

          {plan === undefined ? null : (
            <>
              {plan.authorisationRequired ? (
                <>
                  <p className="lm-hint">{t('claimCapture.authorisationNeeded')}</p>
                  <label className="lm-field">
                    <span className="lm-field__label">{t('claimCapture.referenceLabel')}</span>
                    <input
                      className="lm-input"
                      dir="ltr"
                      value={reference}
                      onChange={(e) => setReference(e.currentTarget.value)}
                    />
                  </label>
                  <label className="lm-field">
                    <span className="lm-field__label">{t('claimCapture.grantedOnLabel')}</span>
                    <input
                      className="lm-input"
                      type="date"
                      dir="ltr"
                      value={grantedOn}
                      onChange={(e) => setGrantedOn(e.currentTarget.value)}
                    />
                  </label>
                </>
              ) : null}

              {confirming ? (
                <>
                  <p className="lm-field__warning">
                    {t('claimCapture.confirmCapture', { panel: plan.panel, plan: plan.name })}
                  </p>
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    disabled={writeId !== null}
                    onClick={() => {
                      void capture()
                    }}
                  >
                    {t('claimCapture.captureButton')}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => setConfirming(false)}
                  >
                    {t('claimCapture.cancel')}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="lm-button lm-button--primary"
                  // An authorisation-required plan refuses without both fields, so the button is
                  // withheld rather than offered and refused into a blocked queue.
                  disabled={
                    plan.authorisationRequired && (reference.trim() === '' || grantedOn === '')
                  }
                  onClick={() => setConfirming(true)}
                >
                  {t('claimCapture.capture')}
                </button>
              )}
            </>
          )}

          <WithheldPlans rows={withheldPlans} t={t} />
        </>
      )}

      <p className="lm-queue-state" role="status">
        {line}
      </p>
    </section>
  )
}

/**
 * The plans this customer is a member of that will not take a claim today, each with the server's
 * own reason.
 *
 * Shown rather than filtered away: "why can I not claim on Acme Gold" is otherwise a phone call to
 * the panel, and the reason is usually something the shop can fix, an unverified membership or a
 * plan whose year has ended.
 */
function WithheldPlans({
  rows,
  t,
}: {
  readonly rows: readonly {
    id: string
    panel: string
    name: string
    code?: string
    message?: string
  }[]
  readonly t: (key: MessageKey, params?: Record<string, string | number>) => string
}): JSX.Element | null {
  if (rows.length === 0) return null
  return (
    <>
      <h3 className="lm-screen__subheading">{t('claimCapture.withheldHeading')}</h3>
      <ul className="lm-claim__withheld-list">
        {rows.map((row) => (
          <li key={row.id}>
            <span>
              <Ltr>{`${row.panel} ${row.name}`}</Ltr>
            </span>
            <span dir="auto">{row.message ?? row.code ?? ''}</span>
          </li>
        ))}
      </ul>
    </>
  )
}
